import React from "react";
import "./css/projected_payments.css";

import PercentField from "../../fields/percent_field";
import CurrencyField from "../../fields/currency_field";
import {
  estimated_cash_to_close,
  first_mortgage_payment_p_and_i,
  initial_escrow_payment_at_closing_homeowners_insurance,
  initial_escrow_payment_at_closing_mortgage_insurance,
  initial_escrow_payment_at_closing_property_taxes,
  initial_escrow_payment_at_closing_property_taxes_mello_roos_monthly,
  loan_amount_first_mortgage,
  loan_term_in_years,
  mortgage_insurance_premium_monthly,
  total_closing_costs,
  total_loan_costs,
  total_other_costs
} from "../../../calculations/loan";
import {
  home_owners_insurance_monthly,
  mello_roos,
  total_hoas_monthly
} from "../../../calculations/property";
import {
  estimated_escrow,
  estimated_taxes_insurance_assessments_other_monthly,
  total_monthly_payment_includes_all
} from "../../../calculations/trid";
import {
  buydown_payment_columns,
  buydown_year_payment,
  buydown_year_rate
} from "../../../calculations/buydown";
import { revised_dti } from "../../../calculations/debt_info";
import { wrap_investment_properties } from "../../../calculations/investment_reo";
import { useDocument } from "../../loan_tools/loan_tool";
import { PURCHASE, REFINANCE } from "../../../constants/document_types";

// Projected Payments for a temporary buydown. Renders one column per stepped year
// (Year 1..N) plus a full-rate column (Years N+1 - 30), with a new Interest Rate row
// and stepped P&I / Total. The Estimated Taxes and Costs at Closing blocks below are
// identical to the standard Projected Payments table.
export default function BuydownProjectedPayment({ loan }) {
  const [
    {
      applicant,
      accountSummaries,
      investment_properties,
      property,
      residences,
      property_to_be_sold,
      is_selling_property
    },
    ,
    { documentType }
  ] = useDocument();
  const isPurchase = documentType === PURCHASE;
  const isRefinance = documentType === REFINANCE;

  const loanAmount = loan_amount_first_mortgage(
    documentType,
    loan,
    property,
    property_to_be_sold,
    is_selling_property
  );
  const termMonths = loan_term_in_years(loan).mul(12);
  const mi = mortgage_insurance_premium_monthly(
    documentType,
    loan,
    property,
    property_to_be_sold,
    is_selling_property
  );
  const escrow = estimated_escrow(documentType, loan, property);

  const cols = buydown_payment_columns(loan);
  const numCols = cols.length;
  const cellSpan = numCols >= 4 ? 2 : numCols === 3 ? 2 : 3;
  const labelSpan = 9 - numCols * cellSpan;

  function piFor(col) {
    return col.isFull
      ? first_mortgage_payment_p_and_i(
          documentType,
          loan,
          property,
          property_to_be_sold,
          is_selling_property
        )
      : buydown_year_payment(loan, col.year, loanAmount, termMonths);
  }

  // "Total Monthly Payment (Includes ALL items below)" adds the extra items beyond
  // P&I + MI + escrow (e.g. additional monthly fees). That extra is constant across
  // years, so add it to each year's Estimated Total.
  const noteTotal = first_mortgage_payment_p_and_i(
    documentType,
    loan,
    property,
    property_to_be_sold,
    is_selling_property
  )
    .add(mi)
    .add(escrow);
  const totalAllExtra = total_monthly_payment_includes_all(
    documentType,
    loan,
    property,
    property_to_be_sold,
    is_selling_property
  ).sub(noteTotal);
  // DTI qualifies at the full note rate (same call the standard LE uses).
  const dti = revised_dti(
    documentType,
    applicant.pay_off_debt_to_qualify,
    applicant.borrowers,
    wrap_investment_properties(property, investment_properties, false),
    accountSummaries,
    property,
    residences,
    loan,
    property_to_be_sold,
    is_selling_property
  );

  return (
    <div className="projected-payments">
      <table className="info-table">
        <colgroup>
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <tbody className="no-top-border with-border with-td-border">
          <tr>
            <td className="no-border summary-header">Projected Payments</td>
            <td className="no-border" colSpan="8" />
          </tr>
          <tr>
            <td className="bold gray-bg" colSpan={labelSpan}>
              Payment Calculation
            </td>
            {cols.map((c, i) => (
              <td
                key={i}
                className="center-align bold gray-bg"
                colSpan={cellSpan}
              >
                {c.label} &ndash;{" "}
                <PercentField
                  naked
                  decimalPlaces={3}
                  value={buydown_year_rate(loan, c.year)}
                />
              </td>
            ))}
          </tr>
          <tr>
            <td className="bold" colSpan={labelSpan}>
              Principal &amp; Interest
            </td>
            {cols.map((c, i) => (
              <td key={i} className="center-align" colSpan={cellSpan}>
                <CurrencyField naked fixedDigits={2} value={piFor(c)} />
              </td>
            ))}
          </tr>
          <tr>
            <td className="bold" colSpan={labelSpan}>
              Mortgage Insurance
            </td>
            {cols.map((c, i) => (
              <td key={i} className="center-align" colSpan={cellSpan}>
                <CurrencyField naked fixedDigits={2} value={mi} />
              </td>
            ))}
          </tr>
          <tr>
            <td className="bold" colSpan={labelSpan}>
              Estimated Escrow
              <div className="smaller-font">(If Impounded - See Below)</div>
              <div className="smaller-font italic indent">
                Amount Can Increase Over Time
              </div>
            </td>
            {cols.map((c, i) => (
              <td
                key={i}
                className="center-align middle-align"
                colSpan={cellSpan}
              >
                <CurrencyField naked fixedDigits={2} value={escrow} />
              </td>
            ))}
          </tr>
          <tr>
            <td className="bold" colSpan={labelSpan}>
              Estimated Total
              <div>Monthly Payment</div>
            </td>
            {cols.map((c, i) => (
              <td
                key={i}
                className="center-align middle-align"
                colSpan={cellSpan}
              >
                <CurrencyField
                  naked
                  fixedDigits={2}
                  value={piFor(c).add(mi).add(escrow)}
                />
              </td>
            ))}
          </tr>
          <tr>
            <td className="bold" colSpan={labelSpan}>
              Total Monthly Payment
              <div className="smaller-font italic">Includes ALL items below</div>
            </td>
            {cols.map((c, i) => (
              <td key={i} className="center-align bold" colSpan={cellSpan}>
                <CurrencyField
                  naked
                  fixedDigits={2}
                  value={piFor(c).add(mi).add(escrow).add(totalAllExtra)}
                />
              </td>
            ))}
          </tr>
          <tr>
            <td className="bold" colSpan={labelSpan}>
              DTI Per Loan Adjustment (PITI)
            </td>
            <td className="center-align" colSpan={numCols * cellSpan}>
              <PercentField naked fixedDigits={2} value={dti} />
            </td>
          </tr>
          <tr>
            <td className="thick-top-border no-bottom-border bold">
              Estimated Taxes, Insurance
            </td>
            <td className="thick-top-border no-border" colSpan="2" />
            <td
              className="center-align thick-top-border no-border bold"
              colSpan="4"
            >
              This estimate includes
            </td>
            <td
              className="thick-top-border right-border no-border bold"
              colSpan="2"
            >
              In escrow?
            </td>
          </tr>
          <tr>
            <td className="right-border no-border bold">&amp; Assessments</td>
            <td className="no-border" colSpan="2" />
            <td className="center-align no-border">
              <CurrencyField
                naked
                fixedDigits={0}
                value={initial_escrow_payment_at_closing_property_taxes_mello_roos_monthly(
                  documentType,
                  loan,
                  property
                )}
              />
            </td>
            <td className="center-align no-border bold">
              {initial_escrow_payment_at_closing_property_taxes_mello_roos_monthly(
                documentType,
                loan,
                property
              ).gt(0) && "X"}
            </td>
            <td className="no-border" colSpan="2">
              Property Taxes
              {mello_roos(property, loan).gt(0) && " + Mello Roos / Other Fee"}
            </td>
            <td className="center-align no-border bold">
              {initial_escrow_payment_at_closing_property_taxes(
                documentType,
                loan,
                property
              ).gt(0)
                ? "YES"
                : "NO"}
            </td>
            <td className="no-border" />
          </tr>
          <tr>
            <td className="right-border no-border smaller-font italic">
              <div className="indent">Amount Can Increase Over Time</div>
            </td>
            <td className="no-border" colSpan="2" />
            <td className="center-align no-border">
              <CurrencyField
                naked
                fixedDigits={0}
                value={home_owners_insurance_monthly(documentType, property)}
              />
            </td>
            <td className="center-align no-border bold">
              {home_owners_insurance_monthly(documentType, property).gt(0) &&
                "X"}
            </td>
            <td className="no-border" colSpan="2">
              Homeowner&apos;s Insurance
            </td>
            <td className="center-align no-border bold">
              {initial_escrow_payment_at_closing_homeowners_insurance(
                documentType,
                loan,
                property
              ).gt(0)
                ? "YES"
                : "NO"}
            </td>
            <td className="no-border" />
          </tr>
          <tr>
            <td className="right-border no-border" rowSpan="4" />
            <td className="center-align no-border">
              <CurrencyField
                naked
                fixedDigits={0}
                value={estimated_taxes_insurance_assessments_other_monthly(
                  documentType,
                  loan,
                  property,
                  property_to_be_sold,
                  is_selling_property
                )}
              />
            </td>
            <td className="no-border" />
            <td className="center-align no-border">
              <CurrencyField
                naked
                fixedDigits={0}
                value={mortgage_insurance_premium_monthly(
                  documentType,
                  loan,
                  property,
                  property_to_be_sold,
                  is_selling_property
                )}
              />
            </td>
            <td className="center-align no-border bold">
              {mortgage_insurance_premium_monthly(
                documentType,
                loan,
                property,
                property_to_be_sold,
                is_selling_property
              ).gt(0) && "X"}
            </td>
            <td className="no-border" colSpan="2">
              <div className="flex-space-between">
                <div>Other:</div>
                <div>
                  {initial_escrow_payment_at_closing_mortgage_insurance(
                    documentType,
                    loan,
                    property,
                    property_to_be_sold,
                    is_selling_property
                  ).gt(0) && "Mortgage Insurance"}
                </div>
              </div>
            </td>
            <td className="center-align no-border bold">
              {initial_escrow_payment_at_closing_mortgage_insurance(
                documentType,
                loan,
                property,
                property_to_be_sold,
                is_selling_property
              ).gt(0) && "YES"}
            </td>
            <td className="no-border" />
          </tr>
          <tr>
            <td className="center-align no-border">a month</td>
            <td className="no-border" />
            <td className="center-align no-border">
              <CurrencyField
                naked
                fixedDigits={0}
                value={total_hoas_monthly(property, loan)}
              />
            </td>
            <td className="center-align no-border bold">
              {total_hoas_monthly(property, loan).gt(0) && "X"}
            </td>
            <td className="no-border" colSpan="2">
              <div className="flex-start-with-spacing">
                <div>Other:</div>
                <div>{total_hoas_monthly(property, loan).gt(0) && "HOA"}</div>
              </div>
            </td>
            <td className="no-border" colSpan="2" />
          </tr>
          <tr>
            <td className="no-border" rowSpan="2" colSpan="4" />
            <td className="no-border italic" colSpan="4">
              See Section G on page 2 for escrowed property costs. You must
            </td>
          </tr>
          <tr>
            <td className="no-border italic" colSpan="4">
              pay for other property costs separately.
            </td>
          </tr>
          <tr>
            <td className="no-border summary-header">Costs at Closing</td>
            <td className="no-border" colSpan="8" />
          </tr>
          <tr>
            <td className="middle-align bold top-border" rowSpan="2">
              Estimated Closing Costs
            </td>
            <td
              className="middle-align center-align no-right-border top-border"
              rowSpan="2"
            >
              <CurrencyField
                naked
                fixedDigits={0}
                value={total_closing_costs(
                  documentType,
                  loan,
                  property,
                  property_to_be_sold,
                  is_selling_property
                )}
              />
            </td>
            <td className="no-border top-border" colSpan="6">
              <div className="flex-start-with-spacing">
                <div>Includes</div>
                <div>
                  <CurrencyField
                    naked
                    fixedDigits={0}
                    value={total_loan_costs(
                      documentType,
                      loan,
                      property,
                      property_to_be_sold,
                      is_selling_property
                    )}
                  />
                </div>
                <div>in Loan Costs +</div>
                <div>
                  <CurrencyField
                    naked
                    fixedDigits={0}
                    value={total_other_costs(
                      documentType,
                      loan,
                      property,
                      property_to_be_sold,
                      is_selling_property
                    )}
                  />
                </div>
                <div>Other Costs</div>
              </div>
            </td>
            <td className="no-border right-border top-border" />
          </tr>
          <tr>
            <td className="no-border" />
            <td className="no-border right-border" colSpan="6">
              (in Lender Credits). See details on page 2.
            </td>
          </tr>
          <tr>
            <td className="middle-align bold" rowSpan="2">
              Estimated Cash to Close
            </td>
            <td
              className="middle-align center-align no-right-border"
              rowSpan="2"
            >
              <CurrencyField
                naked
                fixedDigits={0}
                value={estimated_cash_to_close(
                  documentType,
                  loan,
                  property,
                  property_to_be_sold,
                  is_selling_property,
                  accountSummaries,
                  residences
                )}
              />
            </td>
            <td className="no-left-border no-bottom-border" colSpan="7">
              Includes Closing Costs.
              <span className="italic">
                {" "}
                See Calculating Cash to Close on page 2 for details.
              </span>
            </td>
          </tr>
          <tr>
            <td className="center-align no-border bold">
              {isPurchase &&
                estimated_cash_to_close(
                  documentType,
                  loan,
                  property,
                  property_to_be_sold,
                  is_selling_property,
                  accountSummaries,
                  residences
                ).gt(0) &&
                "X"}
              {isRefinance &&
                estimated_cash_to_close(
                  documentType,
                  loan,
                  property,
                  property_to_be_sold,
                  is_selling_property,
                  accountSummaries,
                  residences
                ).lt(0) &&
                "X"}
            </td>
            <td className="no-border">From</td>
            <td className="center-align no-border bold">
              {isPurchase &&
                estimated_cash_to_close(
                  documentType,
                  loan,
                  property,
                  property_to_be_sold,
                  is_selling_property,
                  accountSummaries,
                  residences
                ).lt(0) &&
                "X"}
              {isRefinance &&
                estimated_cash_to_close(
                  documentType,
                  loan,
                  property,
                  property_to_be_sold,
                  is_selling_property,
                  accountSummaries,
                  residences
                ).gt(0) &&
                "X"}
            </td>
            <td className="no-border">To Borrower</td>
            <td className="no-border" colSpan="3" />
          </tr>
        </tbody>
      </table>
      <div className="consumer-finance-info smaller-font">
        Visit www.consumerfinance.gov/learnmore for general information and
        tools.
      </div>
    </div>
  );
}
